import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BookOpen, ExternalLink, Check, ChevronRight } from 'lucide-react';
import { Sheet } from '../../ui/organisms/Sheet/Sheet';
import { Button } from '@/components/ui/atoms/Button';
import {
  getLearningResources,
  openLearningResource,
  completeLearningResource,
  LearningResource,
} from '../../../services/career-advisor.service';

interface LearningResourcesProps {
  invitationToken: string;
}

interface ResourceCardProps {
  resource: LearningResource;
  onOpen: () => void;
  onToggleDone: () => void;
  openLabel: string;
  openAgainLabel: string;
  doneLabel: string;
  markDoneLabel: string;
}

/** One learning resource — a card the candidate opens in a new tab. */
const ResourceCard = ({
  resource,
  onOpen,
  onToggleDone,
  openLabel,
  openAgainLabel,
  doneLabel,
  markDoneLabel,
}: ResourceCardProps) => {
  const { title, url, rationale, skill_area, opened, completed } = resource;

  return (
    <div
      className={`rounded-xl border bg-light p-4 transition-colors ${
        completed ? 'border-purple/50' : 'border-light-lila/60'
      }`}
    >
      {skill_area && (
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-purple font-body">
          {skill_area}
        </p>
      )}
      <h6 className="mt-1 font-heading text-sm font-semibold leading-snug text-dark-invirtus">
        {title}
      </h6>
      {rationale && (
        <p className="mt-1.5 text-xs leading-relaxed text-dark-invirtus/60 font-body">
          {rationale}
        </p>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          onClick={onOpen}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-dark-invirtus hover:text-purple font-body"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {opened ? openAgainLabel : openLabel}
        </a>

        <Button
          type={completed ? 'contained' : 'outlined'}
          intent={completed ? 'primary' : 'secondary'}
          size="xs"
          onClick={onToggleDone}
          aria-pressed={completed}
          icon={<Check />}
          label={completed ? doneLabel : markDoneLabel}
        />
      </div>
    </div>
  );
};

/**
 * Post-coaching learning resources — a trigger row that opens a right-side
 * sheet of cards. Resources are generated in the background after the coach
 * picks them, so the trigger first shows a "preparing" state.
 */
export const LearningResources = ({ invitationToken }: LearningResourcesProps) => {
  const { t } = useTranslation('interview');
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const queryKey = ['career-advisor-resources', invitationToken];

  const { data } = useQuery({
    queryKey,
    queryFn: () => getLearningResources(invitationToken),
    // Poll while the background task is still producing the resources.
    // Tight interval (2s) because total generation is now ~5-7s, so a slower
    // poll would dominate the perceived latency.
    refetchInterval: (query) =>
      query.state.data?.generating ? 2000 : false,
  });

  const generating = data?.generating ?? false;
  const resources = data?.resources ?? [];
  const total = resources.length;
  const completedCount = resources.filter((r) => r.completed).length;

  const openMutation = useMutation({
    mutationFn: (id: string) => openLearningResource(invitationToken, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const completeMutation = useMutation({
    mutationFn: (vars: { id: string; completed: boolean }) =>
      completeLearningResource(invitationToken, vars.id, vars.completed),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  // Nothing to surface until generation starts.
  if (!generating && total === 0) return null;

  return (
    <>
      {generating ? (
        <div className="flex items-center gap-3 px-5 py-3.5">
          <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-purple border-t-transparent" />
          <p className="text-xs text-dark-invirtus/60 font-body">
            {t('career_advisor.resources.preparing')}
          </p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-dark-invirtus-soft/50"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-soft/40 text-purple">
            <BookOpen className="h-[18px] w-[18px]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-dark-invirtus">
              {t('career_advisor.resources.title')}
            </p>
            <p className="mt-0.5 text-xs text-dark-invirtus/55 font-body">
              {t('career_advisor.resources.progress', {
                completed: completedCount,
                total,
              })}
            </p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-dark-invirtus/40" />
        </button>
      )}

      <Sheet open={open} onOpenChange={setOpen} size="sm">
        <Sheet.Header>
          <Sheet.Title>{t('career_advisor.resources.title')}</Sheet.Title>
          <Sheet.Description>
            {t('career_advisor.resources.sheet_description')}
          </Sheet.Description>
        </Sheet.Header>
        <Sheet.Body className="space-y-3 bg-background-gray/30">
          {total > 0 && (
            <p className="text-xs font-medium text-dark-invirtus/55 font-body">
              {t('career_advisor.resources.progress_completed', {
                completed: completedCount,
                total,
              })}
            </p>
          )}
          {resources.map((resource) => (
            <ResourceCard
              key={resource.id}
              resource={resource}
              onOpen={() => openMutation.mutate(resource.id)}
              onToggleDone={() =>
                completeMutation.mutate({
                  id: resource.id,
                  completed: !resource.completed,
                })
              }
              openLabel={t('career_advisor.resources.open_resource')}
              openAgainLabel={t('career_advisor.resources.open_again')}
              doneLabel={t('career_advisor.resources.done')}
              markDoneLabel={t('career_advisor.resources.mark_done')}
            />
          ))}
        </Sheet.Body>
      </Sheet>
    </>
  );
};
